import {
  CollisionDetection,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  SortingStrategy,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useCallback } from 'react';

// Measure items once before drag starts to avoid mid-drag jumps
// from sibling size changes (e.g., accordion expand/collapse, conditional warnings)
export const measuringConfig = {
  droppable: {
    strategy: MeasuringStrategy.BeforeDragging,
  },
};

export interface SortableListProps<T, IdType extends string = string> {
  /** Array of items to make sortable. */
  items: T[];
  /**
   * Function to extract the unique ID from each item.
   * This ID must match the `id` passed to `useSortable({ id })` in child components.
   */
  getId: (item: T) => IdType;
  /**
   * Callback fired when an item is dropped in a new position.
   * Receives the old and new indices for use with array reordering (e.g., useFieldArray.move).
   */
  onReorder: (oldIndex: number, newIndex: number) => void;
  /** Content to render inside the sortable context. Use useSortable() on individual items. */
  children: React.ReactNode;
  /** Sorting strategy. Defaults to verticalListSortingStrategy. */
  strategy?: SortingStrategy;
  /** Whether drag and drop is disabled. Disables drag interactions but still renders DndContext for child hooks. */
  disabled?: boolean;
}

// The sensor options live outside the hook because `useSensor` memoizes on
// the options object identity. An inline object would create a new sensor
// list on every render, and dnd-kit then hands every sortable item new
// `listeners`, which defeats memoization of the item components.
const pointerSensorOptions = {
  // Require pointer to move 8px before activating drag, allowing clicks to pass through
  activationConstraint: {
    distance: 8,
  },
};

const keyboardSensorOptions = {
  coordinateGetter: sortableKeyboardCoordinates,
};

/**
 * Shared hook for consistent @dnd-kit sensor configuration.
 * Configures pointer sensor with 8px activation distance (to distinguish clicks from drags)
 * and keyboard sensor for accessibility.
 */
export function useDndKitSensors() {
  return useSensors(
    useSensor(PointerSensor, pointerSensorOptions),
    useSensor(KeyboardSensor, keyboardSensorOptions)
  );
}

const pointerOrKeyboardCollisionDetection: CollisionDetection = (args) =>
  args.pointerCoordinates ? pointerWithin(args) : rectIntersection(args);

/**
 * A wrapper component that provides DndContext and SortableContext with pre-configured sensors.
 * Use this when you need custom item rendering (e.g., accordions, complex layouts).
 * For simple lists, prefer DragAndDropList.
 *
 * @example
 * ```tsx
 * <SortableList items={items} getId={(item) => item.id} onReorder={(oldIdx, newIdx) => move(oldIdx, newIdx)}>
 *   <ul>
 *     {items.map((item) => (
 *       <SortableItem key={item.id} id={item.id}>
 *         {item.name}
 *       </SortableItem>
 *     ))}
 *   </ul>
 * </SortableList>
 * ```
 *
 * @example With useFieldArray
 * ```tsx
 * <SortableList items={fields} getId={(f) => f._id} onReorder={move}>
 *   ...
 * </SortableList>
 * ```
 */
export function SortableList<T, IdType extends string = string>({
  items,
  getId,
  onReorder,
  children,
  strategy = verticalListSortingStrategy,
  disabled = false,
}: SortableListProps<T, IdType>) {
  const sensors = useDndKitSensors();

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) return;

      const oldIndex = items.findIndex((item) => getId(item) === active.id);
      const newIndex = items.findIndex((item) => getId(item) === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        onReorder(oldIndex, newIndex);
      }
    },
    [items, getId, onReorder]
  );

  // Always render DndContext so child useSortable hooks don't crash.
  // When disabled, pass empty sensors array to prevent drag interactions.
  // Pointer drops outside the list cancel. Keyboard reorders use the translated
  // item rectangle because they do not have pointer coordinates.
  return (
    <DndContext
      sensors={disabled ? [] : sensors}
      collisionDetection={pointerOrKeyboardCollisionDetection}
      onDragEnd={disabled ? undefined : handleDragEnd}
      measuring={measuringConfig}
    >
      <SortableContext items={items.map(getId)} strategy={strategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
}
